import { useEffect, useMemo, useRef, useState } from 'react';
import { rovingTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { interpolate } from './format';
import { fetchTaxonomy } from '../taxonomy/taxonomy-api';
import { fetchSelectionTags } from './bulk-api';
import { ApiError } from './api';

/**
 * Modale de TAGS EN MASSE (LOT 4 + retrait) : deux modes, choisis par un segment en tête —
 *
 *  - « Ajouter » (défaut) : liste tout le vocabulaire EXISTANT de la bibliothèque
 *    (`GET …/taxonomy`, aucune création de tag ici) → applique les tags cochés à la sélection.
 *  - « Retirer » : liste UNIQUEMENT les tags réellement PRÉSENTS sur la sélection
 *    (`POST …/selection-tags`, avec « sur N média(s) ») → détache les tags cochés.
 *
 * Vraie boîte de dialogue (`role="dialog"` + `aria-modal`) calquée sur `BulkRatingModal` :
 * focus initial, restauration du focus, verrou de défilement, Échap ferme (sauf pendant
 * l'appel réseau), fermeture au clic sur le fond. Le caller l'enveloppe dans un `<Portal>`
 * (l'en-tête porte `backdrop-blur`).
 *
 * La liste est chargée PAR la modale (au montage et à chaque changement de mode) — aucune
 * dépendance à un état parent. État vide : invite à gérer la taxonomie (Ajouter) ou signale
 * qu'aucun tag n'est posé sur la sélection (Retirer). La case cochée est signalée par une
 * COCHE + `aria-checked` (jamais la couleur seule).
 */

/** Mode de la modale : ajouter des tags à la sélection, ou en retirer. */
export type BulkTagMode = 'add' | 'remove';

/** Entrée normalisée de la liste : `count` = usages (Ajouter) ou présence sur la sélection (Retirer). */
interface Entry {
  id: string;
  label: string;
  count: number;
}

interface BulkTagModalProps {
  libraryId: string;
  /** Ids des assets de la sélection (source du « Retirer » + du décompte de l'intro). */
  assetIds: readonly string[];
  /**
   * Appelé avec les ids de tags choisis (non vide) ET le mode courant — l'appel réseau
   * (ajout ou retrait) est géré par le caller.
   */
  onApply: (tagIds: string[], mode: BulkTagMode) => void;
  onClose: () => void;
  /** Appel réseau d'application (ajout/retrait) en cours (désactive les contrôles). */
  busy: boolean;
  /** Message d'erreur d'APPLICATION (403 lecture seule ou échec générique), déjà traduit. */
  error?: string | undefined;
}

export function BulkTagModal({
  libraryId,
  assetIds,
  onApply,
  onClose,
  busy,
  error,
}: BulkTagModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const [mode, setMode] = useState<BulkTagMode>('add');
  // Bascule locale (rien d'écrit avant « Appliquer ») : la selection SUIT LE FOCUS.
  const onModeKeyDown = useRovingRadioGroup('auto');
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: searchRef, onEscape: !busy ? onClose : undefined });
  const count = assetIds.length;

  // Repart d'une liste vierge à chaque changement de mode (choix + recherche remis à zéro).
  useEffect(() => {
    setChosen(new Set());
    setSearch('');
  }, [mode]);

  // Charge la liste adaptée au mode (au montage + à chaque bascule). Annulable au démontage.
  useEffect(() => {
    const ctrl = new AbortController();
    setEntries(null);
    setLoadError(undefined);
    void (async (): Promise<void> => {
      try {
        if (mode === 'add') {
          const result = await fetchTaxonomy(libraryId, ctrl.signal);
          setEntries(
            result.tags.map((tag) => ({ id: tag.id, label: tag.label, count: tag.usageCount })),
          );
        } else {
          const result = await fetchSelectionTags(libraryId, assetIds, ctrl.signal);
          setEntries(
            result.tags.map((tag) => ({ id: tag.id, label: tag.label, count: tag.count })),
          );
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setLoadError(
          err instanceof ApiError && err.status === 403
            ? t('bulkTag.errorForbidden')
            : t('bulkTag.loadError'),
        );
        setEntries([]);
      }
    })();
    return () => ctrl.abort();
  }, [mode, libraryId, assetIds, t]);

  // Filtre client par étiquette (insensible à la casse/aux accents).
  const normalized = search.trim().toLocaleLowerCase();
  const filtered = useMemo(() => {
    if (entries === null) return [];
    if (normalized === '') return entries;
    return entries.filter((entry) =>
      entry.label.toLocaleLowerCase().normalize('NFD').includes(normalized.normalize('NFD')),
    );
  }, [entries, normalized]);

  const toggle = (id: string): void => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loading = entries === null;
  const hasEntries = entries !== null && entries.length > 0;
  const canApply = chosen.size > 0 && !busy;
  const isRemove = mode === 'remove';
  const title = isRemove ? t('bulkTag.titleRemove') : t('bulkTag.title');
  const intro = isRemove ? t('bulkTag.introRemove') : t('bulkTag.intro');

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <TagIcon className="h-6 w-6 text-muted" aria-hidden="true" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('bulkTag.cancel')}
            aria-label={t('bulkTag.cancel')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-3 overflow-hidden p-4 text-sm text-fg">
          {/* Segment Ajouter / Retirer (radiogroup : l'actif porte `aria-checked`, jamais la couleur seule). */}
          <div
            role="radiogroup"
            aria-label={t('bulkTag.modeGroupLabel')}
            onKeyDown={onModeKeyDown}
            className="inline-flex self-start rounded-md border border-border p-0.5"
          >
            {(['add', 'remove'] as const).map((m) => {
              const active = mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  tabIndex={rovingTabIndex(active)}
                  disabled={busy}
                  onClick={() => setMode(m)}
                  className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 ${
                    active
                      ? 'bg-brand text-brand-fg'
                      : 'text-muted hover:bg-surface-2 hover:text-fg'
                  }`}
                >
                  {active && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
                  {m === 'add' ? t('bulkTag.modeAdd') : t('bulkTag.modeRemove')}
                </button>
              );
            })}
          </div>

          <p className="text-muted">{interpolate(intro, { count })}</p>

          {/* Recherche client par nom de tag. */}
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute start-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={busy || !hasEntries}
              aria-label={t('bulkTag.searchLabel')}
              placeholder={t('bulkTag.searchPlaceholder')}
              className="h-9 w-full rounded-md border border-border-strong bg-surface ps-9 pe-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            />
          </div>

          {/* Liste des tags (multi-sélection). */}
          <div
            className="min-h-0 flex-1 overflow-y-auto"
            role="group"
            aria-label={isRemove ? t('bulkTag.listLabelRemove') : t('bulkTag.listLabel')}
          >
            {loading ? (
              <p className="flex items-center gap-2 py-4 text-muted" role="status">
                <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                {t('bulkTag.loading')}
              </p>
            ) : !hasEntries ? (
              <p className="py-4 text-muted">
                {isRemove ? t('bulkTag.emptyRemove') : t('bulkTag.empty')}
              </p>
            ) : filtered.length === 0 ? (
              <p className="py-4 text-muted">{t('bulkTag.noMatch')}</p>
            ) : (
              // Grille responsive : UNE colonne par défaut, DEUX dès `sm:`. Chaque entrée
              // reste un `<label>` enveloppant un checkbox `sr-only`.
              <ul className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                {filtered.map((entry) => {
                  const isChosen = chosen.has(entry.id);
                  return (
                    <li key={entry.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-2">
                        <input
                          type="checkbox"
                          checked={isChosen}
                          disabled={busy}
                          onChange={() => toggle(entry.id)}
                          className="sr-only"
                        />
                        {/* Case visuelle : coche = choisi (icône, jamais la couleur seule). */}
                        <span
                          aria-hidden="true"
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                            isChosen
                              ? 'border-brand bg-brand text-brand-fg'
                              : 'border-border bg-surface'
                          }`}
                        >
                          {isChosen && <CheckIcon className="h-4 w-4" />}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-fg">{entry.label}</span>
                        <span className="shrink-0 text-xs text-muted">
                          {interpolate(
                            isRemove ? t('bulkTag.presentCount') : t('bulkTag.usageCount'),
                            { count: entry.count },
                            locale,
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Nombre de tags choisis (texte, pour les lecteurs d'écran). */}
          <p className="text-xs text-muted" aria-live="polite">
            {interpolate(t('bulkTag.chosenCount'), { count: chosen.size }, locale)}
          </p>

          {(loadError || error) && (
            <p role="alert" className="flex items-start gap-2 text-danger">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{loadError ?? error}</span>
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {t('bulkTag.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onApply([...chosen], mode)}
            disabled={!canApply}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {isRemove
              ? busy
                ? t('bulkTag.removing')
                : t('bulkTag.removeApply')
              : busy
                ? t('bulkTag.applying')
                : t('bulkTag.apply')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
